import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import { ChevronDown, Check, Columns3, X } from 'lucide-react';
import { clsx } from 'clsx';
import WorkspacePill from '../../components/layout/WorkspacePill';
import BusinessFilter from '../../components/layout/BusinessFilter';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { sourcesApi } from '../../services/sources-api';
import {
  viewerApi,
  type ViewerLevel,
  type ViewerRow,
} from '../../services/viewer-api';

const PAGE_SIZE = 50;
const TABS: Array<{ id: ViewerLevel; label: string }> = [
  { id: 'records', label: 'Records' },
  { id: 'details', label: 'Details' },
  { id: 'options', label: 'Options' },
];
const TYPE_CHIP: Record<string, string> = {
  currency: '$',
  count: '#',
  percentage: '%',
  date: 'date',
  category: 'label',
  name: 'text',
  text: 'text',
  boolean: 'flag',
  reference: 'ref',
  identifier: 'id',
};
const WINDOWS = [
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
  { days: 0, label: 'All time' },
];

interface RelFilter {
  id: string;
  label: string;
}

const DataViewerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const accountParam = searchParams.get('account');

  const { data: picker } = useQuery({
    queryKey: ['viewerAccounts'],
    queryFn: () => sourcesApi.getAccessibleAccounts(),
    staleTime: 60_000,
  });
  // Browsing spans the workspace; this narrows it (§8, page-local).
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const storedSources = useMemo(
    () => (picker?.sources ?? []).filter((s) => s.mode === 'ingestion'),
    [picker],
  );
  const pickerOrgs = useMemo(() => {
    const seen = new Map<string, string>();
    storedSources.forEach((s) => seen.set(s.organizationId, s.organizationName));
    return [...seen].map(([id, name]) => ({ id, name }));
  }, [storedSources]);
  const accounts = useMemo(
    () =>
      storedSources
        .filter((s) => !orgFilter || s.organizationId === orgFilter)
        .flatMap((s) =>
          s.accounts.map((a) => ({
            id: a.id,
            label: s.accounts.length > 1 ? `${s.name} · ${a.name ?? 'Account'}` : s.name,
          })),
        ),
    [storedSources, orgFilter],
  );
  // A filter that hides the pinned account falls back to the first still visible.
  const accountId =
    (accountParam && accounts.some((a) => a.id === accountParam) ? accountParam : null) ??
    accounts[0]?.id ??
    null;

  const [tab, setTab] = useState<ViewerLevel>('records');
  const [recordFilter, setRecordFilter] = useState<RelFilter | null>(null);
  const [detailFilter, setDetailFilter] = useState<RelFilter | null>(null);
  const [sort, setSort] = useState<{ slug: string; dir: 'asc' | 'desc' }>({ slug: 'timestamp', dir: 'desc' });
  const [days, setDays] = useState(30);
  const [offset, setOffset] = useState(0);
  const [pickedCols, setPickedCols] = useState<string[] | null>(null);

  // Reset view state when the account changes.
  useEffect(() => {
    setTab('records');
    setRecordFilter(null);
    setDetailFilter(null);
    setSort({ slug: 'timestamp', dir: 'desc' });
    setOffset(0);
    setPickedCols(readCols(accountId, 'records'));
  }, [accountId]);

  const { data: meta } = useQuery({
    queryKey: ['viewerMeta', accountId],
    queryFn: () => viewerApi.getMeta(accountId!),
    enabled: !!accountId,
  });

  const parentId = tab === 'details' ? recordFilter?.id : tab === 'options' ? detailFilter?.id : undefined;
  const listParams = {
    limit: PAGE_SIZE,
    offset,
    sort: tab === 'records' ? sort.slug : undefined,
    dir: tab === 'records' ? sort.dir : undefined,
    days: tab === 'records' && days > 0 ? days : undefined,
    parentId,
  };
  const { data: page, isLoading } = useQuery({
    queryKey: ['viewerRows', accountId, tab, listParams],
    queryFn: () => viewerApi.list(accountId!, tab, listParams),
    enabled: !!accountId && !!meta,
    placeholderData: keepPreviousData,
  });

  const levelFields = meta?.fields[tab] ?? [];
  const defaultCols =
    tab === 'records'
      ? (meta?.defaultColumns ?? []).filter((s) => s !== 'timestamp')
      : levelFields.map((f) => f.slug);
  const visibleCols = (pickedCols ?? defaultCols).filter((s) => levelFields.some((f) => f.slug === s));
  const shownFields = levelFields.filter((f) => visibleCols.includes(f.slug));

  const switchTab = (next: ViewerLevel): void => {
    setTab(next);
    setOffset(0);
    setPickedCols(readCols(accountId, next));
  };
  const openChildren = (row: ViewerRow): void => {
    const label = rowLabel(row);
    if (tab === 'records') {
      setRecordFilter({ id: row.id, label });
      switchTab('details');
    } else if (tab === 'details') {
      setDetailFilter({ id: row.id, label });
      switchTab('options');
    }
  };
  const toggleCol = (slug: string): void => {
    const next = visibleCols.includes(slug)
      ? visibleCols.filter((s) => s !== slug)
      : [...visibleCols, slug];
    setPickedCols(next);
    writeCols(accountId, tab, next);
  };
  const sortBy = (slug: string): void => {
    if (tab !== 'records') return;
    setOffset(0);
    setSort((prev) =>
      prev.slug === slug ? { slug, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { slug, dir: 'desc' },
    );
  };

  const total = page?.total ?? 0;
  const pageNum = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const relFilter = tab === 'details' ? recordFilter : tab === 'options' ? detailFilter : null;

  return (
    <div className="max-w-7xl mx-auto px-6 pt-7 pb-24">
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Data Viewer</h1>
        <div className="flex items-center gap-2.5">
          <BusinessFilter value={orgFilter} onChange={setOrgFilter} orgs={pickerOrgs} />
          <WorkspacePill />
        </div>
      </div>

      {/* Account scope */}
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mr-0.5">
          Account
        </span>
        {accounts.slice(0, 4).map((a) => (
          <button
            key={a.id}
            onClick={() => setSearchParams({ account: a.id })}
            className={clsx(
              'inline-flex items-center gap-2 rounded-[10px] border px-3 py-1.5 text-[12.5px] font-medium',
              a.id === accountId
                ? 'bg-accent border-primary/45 text-primary font-semibold'
                : 'bg-card border-input text-foreground hover:bg-accent',
            )}
          >
            {a.label}
          </button>
        ))}
        {accounts.length > 4 && (
          <Menu as="div" className="relative">
            <Menu.Button className="inline-flex items-center gap-1.5 rounded-[10px] border border-dashed border-input px-3 py-1.5 text-[12.5px] text-muted-foreground hover:bg-accent">
              {accounts.length} accounts <ChevronDown className="w-3.5 h-3.5" />
            </Menu.Button>
            <Menu.Items className="absolute left-0 top-9 z-20 min-w-[240px] max-h-72 overflow-y-auto bg-card border border-border rounded-[10px] shadow-lg p-1 focus:outline-none">
              {accounts.map((a) => (
                <Menu.Item key={a.id}>
                  {({ active }) => (
                    <button
                      onClick={() => setSearchParams({ account: a.id })}
                      className={clsx(
                        'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left',
                        active && 'bg-muted',
                        a.id === accountId ? 'text-primary font-medium' : 'text-foreground',
                      )}
                    >
                      <span className="flex-1 truncate">{a.label}</span>
                      {a.id === accountId && <Check className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Menu>
        )}
      </div>

      {!accountId ? (
        <div className="mt-10 rounded-lg border border-dashed border-border bg-card px-6 py-16 text-center text-sm text-muted-foreground">
          No stored-copy accounts to browse yet — connect a source first.
        </div>
      ) : (
        <>
          {/* Table tabs */}
          <div className="flex gap-0.5 mt-4 border-b border-border">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                className={clsx(
                  'inline-flex items-center gap-2 px-3.5 py-2 text-[13px] font-medium border-b-2 -mb-px',
                  tab === t.id
                    ? 'text-primary border-primary font-semibold'
                    : 'text-muted-foreground border-transparent hover:text-foreground',
                )}
              >
                {t.label}
                <span className="text-[10.5px] text-muted-foreground tabular-nums">
                  {(meta?.counts[t.id] ?? 0).toLocaleString()}
                </span>
              </button>
            ))}
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {relFilter && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent text-primary font-semibold px-2.5 py-1 text-[11.5px]">
                {tab === 'details' ? 'Record' : 'Item'}: {relFilter.label}
                <button
                  onClick={() => {
                    if (tab === 'details') setRecordFilter(null);
                    else setDetailFilter(null);
                    setOffset(0);
                  }}
                  className="opacity-70 hover:opacity-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <Menu as="div" className="relative">
              <Menu.Button className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent">
                <Columns3 className="w-3.5 h-3.5" /> Fields · {shownFields.length} of {levelFields.length}
              </Menu.Button>
              <Menu.Items className="absolute left-0 top-8 z-20 min-w-[210px] max-h-72 overflow-y-auto bg-card border border-border rounded-[10px] shadow-lg p-1 focus:outline-none">
                {levelFields.map((f) => (
                  <Menu.Item key={f.slug}>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          toggleCol(f.slug);
                        }}
                        className={clsx(
                          'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left text-foreground',
                          active && 'bg-muted',
                        )}
                      >
                        <span
                          className={clsx(
                            'w-3.5 h-3.5 rounded border flex items-center justify-center flex-none',
                            visibleCols.includes(f.slug)
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'border-input',
                          )}
                        >
                          {visibleCols.includes(f.slug) && <Check className="w-2.5 h-2.5" />}
                        </span>
                        <span className="flex-1 truncate">{f.label}</span>
                        <span className="text-[9px] uppercase text-muted-foreground">
                          {TYPE_CHIP[f.dataType] ?? f.dataType}
                        </span>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Menu>
            {tab === 'records' && (
              <Menu as="div" className="relative">
                <Menu.Button className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:bg-accent">
                  {WINDOWS.find((w) => w.days === days)?.label} <ChevronDown className="w-3 h-3" />
                </Menu.Button>
                <Menu.Items className="absolute left-0 top-8 z-20 min-w-[160px] bg-card border border-border rounded-[10px] shadow-lg p-1 focus:outline-none">
                  {WINDOWS.map((w) => (
                    <Menu.Item key={w.days}>
                      {({ active }) => (
                        <button
                          onClick={() => {
                            setDays(w.days);
                            setOffset(0);
                          }}
                          className={clsx(
                            'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-[13px] text-left',
                            active && 'bg-muted',
                            w.days === days ? 'text-primary font-medium' : 'text-foreground',
                          )}
                        >
                          <span className="flex-1">{w.label}</span>
                          {w.days === days && <Check className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Menu>
            )}
            <button
              disabled
              title="Coming soon — CSV export ships in a later phase"
              className="rounded-lg border border-input bg-card px-2.5 py-1 text-xs font-medium text-foreground opacity-50 cursor-not-allowed"
            >
              Export
            </button>
            <span className="ml-auto text-xs text-muted-foreground">read-only</span>
          </div>

          {/* Grid */}
          <div className="mt-2.5 overflow-x-auto rounded-t-[10px] border border-border bg-card">
            {isLoading || !page ? (
              <div className="flex items-center justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : page.rows.length === 0 ? (
              <div className="py-14 text-center text-sm text-muted-foreground">
                No rows {relFilter ? 'match this filter' : days > 0 && tab === 'records' ? 'in this window' : 'yet'}.
              </div>
            ) : (
              <table className="border-collapse text-xs min-w-full">
                <thead>
                  <tr>
                    <Th className="w-11 min-w-[44px] text-center">#</Th>
                    {tab === 'records' && (
                      <Th onClick={() => sortBy('timestamp')} sortable>
                        <HeadCell name="Time" chip="date" sorted={sort.slug === 'timestamp' ? sort.dir : null} />
                      </Th>
                    )}
                    {shownFields.map((f) => (
                      <Th key={f.slug} onClick={() => sortBy(f.slug)} sortable={tab === 'records'}>
                        <HeadCell
                          name={f.label}
                          chip={TYPE_CHIP[f.dataType] ?? f.dataType}
                          sorted={tab === 'records' && sort.slug === f.slug ? sort.dir : null}
                        />
                      </Th>
                    ))}
                    {tab !== 'options' && (
                      <Th>
                        <HeadCell name={tab === 'records' ? 'Items' : 'Options'} chip="rel" sorted={null} />
                      </Th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row, i) => (
                    <tr key={row.id} className="hover:bg-muted/45">
                      <td className="border border-border px-2 py-1.5 text-center text-[10px] text-muted-foreground tabular-nums bg-muted/70">
                        {offset + i + 1}
                      </td>
                      {tab === 'records' && (
                        <Td>{row.timestamp ? formatTs(row.timestamp) : <Null />}</Td>
                      )}
                      {shownFields.map((f) => (
                        <Td key={f.slug} right={isNumericType(f.dataType)}>
                          <CellValue value={row.values[f.slug]} field={f} />
                        </Td>
                      ))}
                      {tab !== 'options' && (
                        <Td>
                          {row.childCount ? (
                            <button
                              className="inline-flex items-center gap-1 text-primary font-semibold text-[11.5px] hover:underline"
                              onClick={() => openChildren(row)}
                            >
                              {row.childCount} {row.childCount === 1 ? 'row' : 'rows'} →
                            </button>
                          ) : (
                            <span className="text-muted-foreground italic text-[11px]">0 rows</span>
                          )}
                        </Td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Status bar */}
          <div className="flex flex-wrap items-center gap-4 rounded-b-[10px] border border-t-0 border-border bg-card px-3.5 py-2 text-[11px] text-muted-foreground">
            <span>
              <b className="text-foreground font-semibold">{total.toLocaleString()}</b> rows
              {relFilter && ' match the filter'}
            </span>
            <span>
              <b className="text-foreground font-semibold">{shownFields.length}</b> of {levelFields.length} mapped
              fields shown
            </span>
            {relFilter && (
              <button
                className="text-primary font-semibold hover:underline"
                onClick={() => switchTab(tab === 'details' ? 'records' : 'details')}
              >
                ← back to {tab === 'details' ? 'Records' : 'Details'}
              </button>
            )}
            <span className="ml-auto flex items-center gap-2">
              <PageBtn disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
                ← Prev
              </PageBtn>
              rows {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} · page {pageNum} of{' '}
              {pageCount.toLocaleString()}
              <PageBtn disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
                Next →
              </PageBtn>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// ── Cells ───────────────────────────────────────────────────────────────

const Th: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  sortable?: boolean;
}> = ({ children, className, onClick, sortable }) => (
  <th
    onClick={sortable ? onClick : undefined}
    className={clsx(
      'sticky top-0 border border-border bg-muted/70 p-0 text-left whitespace-nowrap',
      sortable && 'cursor-pointer select-none',
      className,
    )}
  >
    {children}
  </th>
);

const HeadCell: React.FC<{ name: string; chip: string; sorted: 'asc' | 'desc' | null }> = ({
  name,
  chip,
  sorted,
}) => (
  <span className="flex items-center gap-1.5 px-2.5 py-1.5">
    <span className="text-[11.5px] font-bold text-foreground">{name}</span>
    <span className="text-[8.5px] font-bold uppercase tracking-wide text-muted-foreground bg-card border border-border rounded px-1">
      {chip}
    </span>
    {sorted && <span className="text-[9px] text-muted-foreground">{sorted === 'desc' ? '▼' : '▲'}</span>}
  </span>
);

const Td: React.FC<{ children: React.ReactNode; right?: boolean }> = ({ children, right }) => (
  <td
    className={clsx(
      'border border-border px-2.5 py-1.5 whitespace-nowrap tabular-nums max-w-[260px] overflow-hidden text-ellipsis text-foreground',
      right && 'text-right',
    )}
  >
    {children}
  </td>
);

const Null: React.FC = () => <span className="text-muted-foreground italic text-[11px]">null</span>;

const CellValue: React.FC<{ value: unknown; field: { dataType: string } }> = ({ value, field }) => {
  if (value === null || value === undefined || value === '') return <Null />;
  if (field.dataType === 'currency') {
    const n = Number(value);
    if (Number.isFinite(n)) {
      const text = `${n < 0 ? '−' : ''}$${Math.abs(n).toFixed(2)}`;
      return <span className={n < 0 ? 'text-destructive' : undefined}>{text}</span>;
    }
  }
  if (field.dataType === 'boolean') return <>{value === true || value === 'true' ? '✓' : '—'}</>;
  if (field.dataType === 'category') {
    return (
      <span className="inline-flex text-[10px] font-semibold rounded-full px-2 py-px bg-muted text-muted-foreground">
        {String(value)}
      </span>
    );
  }
  if (field.dataType === 'identifier' || field.dataType === 'reference') {
    return <span className="font-mono text-[11px]">{String(value)}</span>;
  }
  return <>{String(value)}</>;
};

const PageBtn: React.FC<{ disabled: boolean; onClick: () => void; children: React.ReactNode }> = ({
  disabled,
  onClick,
  children,
}) => (
  <button
    disabled={disabled}
    onClick={onClick}
    className="rounded-md border border-input bg-card px-2 py-0.5 text-[11px] text-foreground hover:bg-accent disabled:opacity-40"
  >
    {children}
  </button>
);

// ── Helpers ─────────────────────────────────────────────────────────────

function isNumericType(t: string): boolean {
  return t === 'currency' || t === 'count' || t === 'percentage';
}

function formatTs(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' })} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function rowLabel(row: ViewerRow): string {
  for (const v of Object.values(row.values)) {
    if (typeof v === 'string' && v.length > 0) return v.length > 28 ? `${v.slice(0, 28)}…` : v;
  }
  return row.id.slice(0, 8);
}

function colsKey(accountId: string | null, level: ViewerLevel): string {
  return `zamino_viewer_cols_${accountId}_${level}`;
}

function readCols(accountId: string | null, level: ViewerLevel): string[] | null {
  if (!accountId) return null;
  try {
    const raw = localStorage.getItem(colsKey(accountId, level));
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

function writeCols(accountId: string | null, level: ViewerLevel, cols: string[]): void {
  if (!accountId) return;
  try {
    localStorage.setItem(colsKey(accountId, level), JSON.stringify(cols));
  } catch {
    // localStorage unavailable — column picks just don't persist (Q8/D42).
  }
}

export default DataViewerPage;
